import type { PlayerType } from './resultsData'

export interface SessionStep {
  step: string
  timestamp: number
  data?: Record<string, string>
}

export interface QuizSession {
  id: string
  visitorId: string
  startedAt: number
  device: 'mobile' | 'desktop'
  steps: SessionStep[]
  completed: boolean
  resultType?: PlayerType
  ctaClicked: boolean
}

export interface FunnelStep {
  name: string
  count: number
  ctr: number
  percentOfTotal: number
}

export interface AnswerBreakdown {
  tag: string
  text: string
  count: number
  percent: number
}

export interface Analytics {
  totalSessions: number
  uniqueVisitors: number
  completionRate: number
  ctaClickRate: number
  avgQuestionsAnswered: number
  biggestDropStep: string
  biggestDropPercent: number
  deviceSplit: { mobile: number; desktop: number }
  funnel: FunnelStep[]
  resultDistribution: { type: string; count: number; percent: number }[]
  questionBreakdowns: Record<number, AnswerBreakdown[]>
}

const STORAGE_KEY = 'quiz_analytics_sessions'
const VISITOR_KEY = 'quiz_visitor_id'

function generateId(): string {
  return Math.random().toString(36).substring(2, 10) + Date.now().toString(36)
}

function detectDevice(): 'mobile' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop'
  return window.innerWidth < 768 ? 'mobile' : 'desktop'
}

export function getVisitorId(): string {
  if (typeof window === 'undefined') return generateId()
  let id = localStorage.getItem(VISITOR_KEY)
  if (!id) {
    id = generateId()
    localStorage.setItem(VISITOR_KEY, id)
  }
  return id
}

export function getAllSessions(): QuizSession[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveSessions(sessions: QuizSession[]): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions))
}

export function getOrCreateSession(visitorId: string): QuizSession {
  const sessions = getAllSessions()
  const existing = sessions.find(
    (s) => s.visitorId === visitorId && !s.completed,
  )
  if (existing) return existing

  const session: QuizSession = {
    id: generateId(),
    visitorId,
    startedAt: Date.now(),
    device: detectDevice(),
    steps: [],
    completed: false,
    ctaClicked: false,
  }
  sessions.push(session)
  saveSessions(sessions)
  return session
}

export function trackStep(
  sessionId: string,
  step: string,
  data?: Record<string, string>,
): void {
  const sessions = getAllSessions()
  const session = sessions.find((s) => s.id === sessionId)
  if (!session) return

  const alreadyTracked = session.steps.some((s) => s.step === step)
  if (alreadyTracked) return

  session.steps.push({ step, timestamp: Date.now(), data })
  saveSessions(sessions)
}

export function completeSession(
  sessionId: string,
  resultType: PlayerType,
): void {
  const sessions = getAllSessions()
  const session = sessions.find((s) => s.id === sessionId)
  if (!session || session.completed) return

  session.completed = true
  session.resultType = resultType
  saveSessions(sessions)
}

export function trackCTAClick(sessionId: string): void {
  const sessions = getAllSessions()
  const session = sessions.find((s) => s.id === sessionId)
  if (!session) return

  // Deduplicate across ALL sessions for this visitor (unique per visitor)
  const visitorAlreadyClicked = sessions.some(
    (s) => s.visitorId === session.visitorId && s.ctaClicked,
  )
  if (visitorAlreadyClicked) return

  session.ctaClicked = true
  if (!session.steps.some((s) => s.step === 'cta_clicked')) {
    session.steps.push({ step: 'cta_clicked', timestamp: Date.now() })
  }
  saveSessions(sessions)
}

export function clearAllSessions(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(STORAGE_KEY)
}

export function computeAnalytics(sessions: QuizSession[]): Analytics {
  const totalSessions = sessions.length

  const uniqueVisitorIds = new Set(sessions.map((s) => s.visitorId))
  const uniqueVisitors = uniqueVisitorIds.size

  const completedSessions = sessions.filter((s) => s.completed)
  const completionRate =
    totalSessions > 0 ? (completedSessions.length / totalSessions) * 100 : 0

  const ctaClicks = sessions.filter((s) => s.ctaClicked).length
  const ctaClickRate =
    completedSessions.length > 0
      ? (ctaClicks / completedSessions.length) * 100
      : 0

  const questionSteps = sessions.map(
    (s) => s.steps.filter((st) => st.step.startsWith('question_')).length,
  )
  const avgQuestionsAnswered =
    totalSessions > 0
      ? questionSteps.reduce((a, b) => a + b, 0) / totalSessions
      : 0

  const mobileCount = sessions.filter((s) => s.device === 'mobile').length
  const desktopCount = totalSessions - mobileCount
  const deviceSplit = {
    mobile:
      totalSessions > 0 ? Math.round((mobileCount / totalSessions) * 100) : 0,
    desktop:
      totalSessions > 0 ? Math.round((desktopCount / totalSessions) * 100) : 0,
  }

  // Funnel
  const funnelStepNames = [
    'quiz_start',
    ...Array.from({ length: 14 }, (_, i) => `question_${i + 1}`),
    'quiz_complete',
    'result_viewed',
    'cta_clicked',
  ]

  const funnelCounts = funnelStepNames.map(
    (name) =>
      sessions.filter((s) => s.steps.some((st) => st.step === name)).length,
  )

  const funnel: FunnelStep[] = funnelStepNames.map((name, i) => {
    const count = funnelCounts[i]
    const prev = i > 0 ? funnelCounts[i - 1] : totalSessions
    const ctr = prev > 0 ? (count / prev) * 100 : 0
    const percentOfTotal = totalSessions > 0 ? (count / totalSessions) * 100 : 0

    let displayName = name
    if (name === 'quiz_start') displayName = 'Inicio'
    else if (name.startsWith('question_'))
      displayName = `P${name.split('_')[1]}`
    else if (name === 'quiz_complete') displayName = 'Completo'
    else if (name === 'result_viewed') displayName = 'Resultado'
    else if (name === 'cta_clicked') displayName = 'CTA'

    return { name: displayName, count, ctr, percentOfTotal }
  })

  // Biggest drop
  let biggestDropStep = 'N/A'
  let biggestDropPercent = 0
  for (let i = 1; i < funnelCounts.length; i++) {
    if (funnelCounts[i - 1] > 0) {
      const drop =
        ((funnelCounts[i - 1] - funnelCounts[i]) / funnelCounts[i - 1]) * 100
      if (drop > biggestDropPercent) {
        biggestDropPercent = drop
        biggestDropStep = funnel[i].name
      }
    }
  }

  // Result distribution
  const resultCounts: Record<string, number> = {}
  completedSessions.forEach((s) => {
    if (s.resultType) {
      resultCounts[s.resultType] = (resultCounts[s.resultType] || 0) + 1
    }
  })
  const resultDistribution = Object.entries(resultCounts).map(
    ([type, count]) => ({
      type,
      count,
      percent:
        completedSessions.length > 0
          ? Math.round((count / completedSessions.length) * 100)
          : 0,
    }),
  )

  // Question breakdowns
  const questionBreakdowns: Record<number, AnswerBreakdown[]> = {}
  for (let q = 1; q <= 14; q++) {
    const answerCounts: Record<string, { count: number; text: string }> = {}
    sessions.forEach((s) => {
      const step = s.steps.find((st) => st.step === `question_${q}`)
      if (step?.data?.tag) {
        const tag = step.data.tag
        const text = step.data.text || tag
        if (!answerCounts[tag]) answerCounts[tag] = { count: 0, text }
        answerCounts[tag].count++
      }
    })
    const total = Object.values(answerCounts).reduce((a, b) => a + b.count, 0)
    questionBreakdowns[q] = Object.entries(answerCounts)
      .map(([tag, { count, text }]) => ({
        tag,
        text,
        count,
        percent: total > 0 ? Math.round((count / total) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count)
  }

  return {
    totalSessions,
    uniqueVisitors,
    completionRate,
    ctaClickRate,
    avgQuestionsAnswered,
    biggestDropStep,
    biggestDropPercent,
    deviceSplit,
    funnel,
    resultDistribution,
    questionBreakdowns,
  }
}
